/**
 * Frontend Call Graph Page (Issues #864 Phase B, #867)
 *
 * Reuses all Call Graph explorer views (explorer, list, matrix, tree,
 * subgraph) by converting the FCG data into CallGraphData shape.
 * Adds stitch-status overlays via the stitched-mappings endpoint.
 *
 * Data source: GET /projects/:id/frontend-call-graph
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { CallGraphData, CallGraphNode, CallGraphEdge, FunctionNodeType } from '@veodiagram/shared';
import { api } from '../services/api';
import {
  FunctionExplorer,
  FunctionList,
  DependencyMatrix,
  FocusedSubgraph,
  TreeView,
} from '../components/callGraph';

type ViewMode = 'explorer' | 'list' | 'matrix' | 'subgraph' | 'tree';

// ─── Convert FCG → CallGraphData ───

interface FCGApiCallSite {
  callSiteId: string;
  callSiteKey: { filePath: string; enclosingSymbol: string; urlPattern: string; method: string; wrapperChainHash: string };
  filePath: string;
  lineStart: number;
  lineEnd: number;
  urlPattern: string;
  method: string;
  library: string;
  wrapperChain: string[];
  isDynamicEndpoint: boolean;
}

interface FCGNode {
  id: string;
  kind: string;
  component?: string;
  handlerName?: string;
  functionName?: string;
  hookName?: string;
  interactionType?: string;
  triggerElement?: string;
  elementText?: string;
  callSite?: FCGApiCallSite;
  filePath?: string;
  line?: number;
}

interface FCGEdge {
  from: string;
  to: string;
  kind: string;
}

interface FCGData {
  nodes: Record<string, FCGNode>;
  edges: FCGEdge[];
  apiCallSites: FCGApiCallSite[];
  stats: { uiElements: number; handlers: number; functions: number; hooks: number; callSites: number; dynamicCallSites: number };
}

function shortenPath(p: string): string {
  const parts = p.split('/');
  return parts.length <= 3 ? p : '.../' + parts.slice(-3).join('/');
}

function fcgNodeName(node: FCGNode): string {
  switch (node.kind) {
    case 'ApiCallSite': return `${node.callSite?.method?.toUpperCase() ?? '?'} ${node.callSite?.urlPattern ?? '?'}`;
    case 'UIElement': return node.elementText || node.triggerElement || node.interactionType || 'UI';
    case 'Handler': return node.handlerName || 'handler';
    case 'Function': return node.functionName || 'fn';
    case 'Hook': return node.hookName || 'hook';
    default: return node.id;
  }
}

function fcgNodeType(kind: string): FunctionNodeType {
  switch (kind) {
    case 'UIElement': return 'entry';
    case 'Handler': return 'method';
    case 'Hook': return 'arrow';
    case 'ApiCallSite': return 'external';
    default: return 'function';
  }
}

function convertFCGToCallGraphData(
  fcg: FCGData,
  stitchMap: Record<string, { method: string; path: string; source: string; confidence: number }>,
): CallGraphData {
  const nodes: CallGraphNode[] = [];
  const edges: CallGraphEdge[] = [];
  const entryPoints: string[] = [];
  const leafNodes: string[] = [];
  const hotspots: Array<{ id: string; callerCount: number }> = [];

  // Track edges for leaf/entry detection
  const hasOutgoing = new Set<string>();
  const hasIncoming = new Set<string>();

  // Convert FCG nodes
  for (const [id, node] of Object.entries(fcg.nodes)) {
    const name = fcgNodeName(node);
    const fp = node.callSite?.filePath || node.filePath || '';
    const mod = fp ? shortenPath(fp) : 'unknown';

    // Stitch status in description
    let desc = node.kind;
    if (node.kind === 'ApiCallSite' && node.callSite) {
      const stitch = stitchMap[node.callSite.callSiteId];
      if (stitch) desc = `stitched (${stitch.source}) → ${stitch.method} ${stitch.path}`;
      else desc = node.callSite.isDynamicEndpoint ? 'dynamic endpoint' : 'unstitched';
    }

    nodes.push({
      id,
      name,
      qualifiedName: name,
      module: mod,
      type: fcgNodeType(node.kind),
      isAsync: false,
      isEntryPoint: node.kind === 'UIElement',
      isExternal: node.kind === 'ApiCallSite',
      description: desc,
      calledCount: 0,
      callCount: 0,
      location: fp ? { file: fp, startLine: node.callSite?.lineStart || node.line || 0, endLine: node.callSite?.lineEnd || node.line || 0 } : undefined,
    });

    if (node.kind === 'UIElement') entryPoints.push(id);
  }

  // Convert FCG edges
  for (let i = 0; i < fcg.edges.length; i++) {
    const e = fcg.edges[i];
    if (!fcg.nodes[e.from] || !fcg.nodes[e.to]) continue;
    edges.push({
      id: `fcg-edge-${i}`,
      source: e.from,
      target: e.to,
      isAsync: false,
      isMethodCall: e.kind === 'invokes',
      callCount: 1,
      callSites: [],
      confidence: 1,
    });
    hasOutgoing.add(e.from);
    hasIncoming.add(e.to);
  }

  // Identify leaves (nodes with no outgoing edges)
  for (const n of nodes) {
    if (!hasOutgoing.has(n.id)) leafNodes.push(n.id);
  }

  // Compute calledCount/callCount
  const calledCountMap = new Map<string, number>();
  const callCountMap = new Map<string, number>();
  for (const e of edges) {
    calledCountMap.set(e.target, (calledCountMap.get(e.target) || 0) + 1);
    callCountMap.set(e.source, (callCountMap.get(e.source) || 0) + 1);
  }
  for (const n of nodes) {
    n.calledCount = calledCountMap.get(n.id) || 0;
    n.callCount = callCountMap.get(n.id) || 0;
    if (n.calledCount >= 3) hotspots.push({ id: n.id, callerCount: n.calledCount });
  }
  hotspots.sort((a, b) => b.callerCount - a.callerCount);

  return {
    metadata: { title: 'Frontend Call Graph', diagramType: 'callGraph' as const, c4Level: 'code' as const },
    nodes,
    edges,
    entryPoints,
    leafFunctions: leafNodes,
    criticalPaths: [],
    hotspots: hotspots.slice(0, 20),
    unusedFunctions: [],
    stats: {
      totalFunctions: nodes.length,
      internalFunctions: nodes.filter(n => !n.isExternal).length,
      externalCalls: nodes.filter(n => n.isExternal).length,
      avgCallsPerFunction: nodes.length > 0 ? edges.length / nodes.length : 0,
      maxCallDepth: 0,
    },
  };
}

// ─── Main Component ───

export function FrontendGraphPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [callGraphData, setCallGraphData] = useState<CallGraphData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('explorer');
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [subgraphRootId, setSubgraphRootId] = useState<string | null>(null);
  const [subgraphHistory, setSubgraphHistory] = useState<string[]>([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [typeFilters, setTypeFilters] = useState<Set<FunctionNodeType>>(new Set());

  // Stitch overlay stats
  const [stitchStats, setStitchStats] = useState({ stitched: 0, unstitched: 0 });

  const modules = useMemo(() => {
    if (!callGraphData) return [];
    const s = new Set<string>();
    for (const n of callGraphData.nodes) s.add(n.module);
    return Array.from(s).sort();
  }, [callGraphData]);

  const filteredCallGraphData = useMemo((): CallGraphData | null => {
    if (!callGraphData) return null;
    if (!moduleFilter && typeFilters.size === 0) return callGraphData;
    const filteredNodes = callGraphData.nodes.filter(n => {
      if (moduleFilter && n.module !== moduleFilter) return false;
      if (typeFilters.size > 0 && !typeFilters.has(n.type)) return false;
      return true;
    });
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = callGraphData.edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    return { ...callGraphData, nodes: filteredNodes, edges: filteredEdges };
  }, [callGraphData, moduleFilter, typeFilters]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [fcgRes, mappings] = await Promise.all([
        api.getFrontendCallGraph(projectId),
        api.getStitchedMappings(projectId).catch(() => ({ callerToEndpoint: {}, endpointToCallers: {} })),
      ]);
      const fcg = fcgRes.frontendCallGraph as unknown as FCGData;
      if (!fcg || !fcg.nodes) {
        setError('No frontend call graph found. Run analysis to generate one.');
        return;
      }
      const sm: Record<string, { method: string; path: string; source: string; confidence: number }> = mappings.callerToEndpoint ?? {};
      const cgData = convertFCGToCallGraphData(fcg, sm);
      setCallGraphData(cgData);

      // Compute stitch stats
      const callSites = fcg.apiCallSites ?? [];
      setStitchStats({
        stitched: callSites.filter(cs => sm[cs.callSiteId]).length,
        unstitched: callSites.filter(cs => !sm[cs.callSiteId]).length,
      });

      if (cgData.entryPoints.length > 0) setSelectedFunctionId(cgData.entryPoints[0]);
      else if (cgData.nodes.length > 0) setSelectedFunctionId(cgData.nodes[0].id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load frontend call graph');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelectFunction = useCallback((id: string) => setSelectedFunctionId(id), []);
  const handleViewInExplorer = useCallback((id: string) => { setSelectedFunctionId(id); setViewMode('explorer'); }, []);
  const handleFocusFunction = useCallback((id: string) => { setSubgraphRootId(id); setSubgraphHistory([]); setViewMode('subgraph'); }, []);
  const handleSelectEdge = useCallback((sourceId: string) => { setSelectedFunctionId(sourceId); setViewMode('explorer'); }, []);
  const handleSubgraphBack = useCallback(() => {
    if (subgraphHistory.length > 0) {
      setSubgraphRootId(subgraphHistory[subgraphHistory.length - 1]);
      setSubgraphHistory(h => h.slice(0, -1));
    }
  }, [subgraphHistory]);
  const handleSubgraphNavigateToIndex = useCallback((i: number) => {
    const target = subgraphHistory[i];
    if (target) { setSubgraphHistory(prev => prev.slice(0, i)); setSubgraphRootId(target); }
  }, [subgraphHistory]);
  const handleCloseSubgraph = useCallback(() => { setViewMode('explorer'); setSubgraphRootId(null); setSubgraphHistory([]); }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600" />
          <p className="text-slate-600 dark:text-slate-400">Loading frontend call graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Error</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
          <div className="flex gap-4 justify-center">
            <button onClick={fetchData} className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700">Retry</button>
            <Link to={`/projects/${projectId}`} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded">Back</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!callGraphData) return null;

  return (
    <div className="h-screen bg-slate-50 dark:bg-gray-900 flex flex-col overflow-hidden" data-testid="frontend-graph-page">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/projects/${projectId}`} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
              ← Back to Project
            </Link>
            <h1 className="text-xl font-semibold text-slate-800 dark:text-white" data-testid="page-title">
              Frontend Call Graph
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {(['explorer', 'list', 'matrix', 'tree', 'subgraph'] as ViewMode[]).map(v => (
                <button
                  key={v}
                  onClick={() => { if (v === 'subgraph') { setSubgraphRootId(selectedFunctionId); } setViewMode(v); }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors capitalize ${
                    viewMode === v
                      ? 'bg-white dark:bg-gray-600 shadow text-cyan-600 dark:text-cyan-400 font-medium'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <button onClick={fetchData} className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded hover:bg-cyan-700">Refresh</button>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-2 flex items-center gap-4 text-sm flex-wrap" data-testid="stats-bar">
        <span><strong>{filteredCallGraphData?.nodes.length ?? 0}</strong> nodes</span>
        <span><strong>{filteredCallGraphData?.edges.length ?? 0}</strong> edges</span>
        <span><strong>{callGraphData.entryPoints.length}</strong> UI elements</span>
        {stitchStats.stitched + stitchStats.unstitched > 0 && (
          <>
            <span className="text-green-600 dark:text-green-400" data-testid="stitched-count"><strong>{stitchStats.stitched}</strong> stitched</span>
            <span className="text-red-600 dark:text-red-400" data-testid="unstitched-count"><strong>{stitchStats.unstitched}</strong> unstitched</span>
          </>
        )}
        <div className="flex-1" />
        <div className={`flex items-center gap-2 flex-wrap transition-opacity ${(viewMode === 'explorer' || viewMode === 'subgraph') ? 'opacity-40 pointer-events-none' : ''}`}>
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="px-2 py-1 border border-slate-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700">
            <option value="">All modules</option>
            {modules.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {(['function', 'method', 'arrow', 'entry', 'external'] as FunctionNodeType[]).map(t => (
            <button
              key={t}
              onClick={() => setTypeFilters(prev => { const next = new Set(prev); if (next.has(t)) next.delete(t); else next.add(t); return next; })}
              className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${typeFilters.has(t) ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white dark:bg-gray-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-gray-600'}`}
            >
              {t === 'entry' ? 'UI' : t === 'method' ? 'handler' : t === 'arrow' ? 'hook' : t === 'external' ? 'call-site' : t}
            </button>
          ))}
          {(moduleFilter || typeFilters.size > 0) && (
            <button onClick={() => { setModuleFilter(''); setTypeFilters(new Set()); }} className="px-2 py-0.5 text-xs text-cyan-600 hover:underline">Clear</button>
          )}
        </div>
      </div>

      {/* Main content — reuse Call Graph views */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'explorer' && filteredCallGraphData && (
          <FunctionExplorer data={filteredCallGraphData} selectedFunctionId={selectedFunctionId || undefined} onSelectFunction={handleSelectFunction} onFocusFunction={handleFocusFunction} />
        )}
        {viewMode === 'list' && filteredCallGraphData && (
          <FunctionList data={filteredCallGraphData} selectedFunctionId={selectedFunctionId || undefined} onSelectFunction={handleSelectFunction} onViewInExplorer={handleViewInExplorer} onViewSubgraph={handleFocusFunction} />
        )}
        {viewMode === 'matrix' && filteredCallGraphData && (
          <DependencyMatrix data={filteredCallGraphData} onSelectFunction={handleSelectFunction} onSelectEdge={handleSelectEdge} />
        )}
        {viewMode === 'tree' && filteredCallGraphData && (
          <TreeView data={filteredCallGraphData} selectedFunctionId={selectedFunctionId || undefined} onSelectFunction={handleSelectFunction} onViewInExplorer={handleViewInExplorer} onViewSubgraph={handleFocusFunction} />
        )}
        {viewMode === 'subgraph' && subgraphRootId && filteredCallGraphData && (
          <FocusedSubgraph
            data={filteredCallGraphData}
            rootFunctionId={subgraphRootId}
            onSelectFunction={(id) => { setSubgraphHistory(h => [...h, subgraphRootId!]); setSubgraphRootId(id); }}
            onBack={handleSubgraphBack}
            onClose={handleCloseSubgraph}
            historyFunctionIds={subgraphHistory}
            onNavigateToHistoryIndex={handleSubgraphNavigateToIndex}
          />
        )}
      </div>
    </div>
  );
}
